/**
 * Numeric input parsing for V2 admin forms.
 *
 * The wizard + edit sheets store all numeric fields as strings (text
 * inputs round-trip free-form text). At save time we coerce to numbers
 * before sending to PostgREST. The previous pattern was:
 *
 *   master_rent: form.master_rent === '' ? null : Number(form.master_rent)
 *
 * which silently turned non-numeric input ("£250", "2.5k", "abc") into
 * NaN — Postgres then rejects the insert with a generic "invalid input
 * syntax for type numeric" error, the user gets a vague toast, and the
 * actual cause (their typo) is invisible.
 *
 * These helpers return a discriminated result so the call site can
 * branch:
 *   - ok: true  → safe to send to the DB
 *   - ok: false → toast / wizard error with the raw input quoted back
 *
 * Both helpers reject negatives because every field they're applied to
 * (master_rent, room price, room size_sqm) is logically non-negative.
 * If a field that legitimately accepts negatives shows up later, add a
 * `parseSignedNumber` rather than overloading these.
 */
export type NumericParseResult =
  // `raw?: never`: strictNullChecks is off, so `if (!r.ok)` cannot narrow
  // this union, and reading `r.raw` there needs the key on both members.
  { ok: true; value: number | null; raw?: never } | { ok: false; raw: string };

/**
 * Parse a nullable numeric field (e.g. master_rent, size_sqm). Empty
 * input → null. Whitespace-only also → null. Non-finite or negative
 * input → { ok: false }.
 */
export function parseOptionalNumber(input: string): NumericParseResult {
  const trimmed = input.trim();
  if (trimmed === "") return { ok: true, value: null };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return { ok: false, raw: trimmed };
  return { ok: true, value: n };
}

/**
 * Parse a required numeric field with a fallback default for empty
 * input (e.g. room price, which defaults to 0 in the create wizard
 * rather than failing the insert). Non-finite or negative input still
 * fails the parse — the default is only used when the input is empty.
 */
export function parseRequiredNumber(
  input: string,
  defaultValue: number
): NumericParseResult {
  const trimmed = input.trim();
  if (trimmed === "") return { ok: true, value: defaultValue };
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return { ok: false, raw: trimmed };
  return { ok: true, value: n };
}
